import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
} from "@mui/material";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { planBillingApi } from "../../api/planBilling";
import MercadoPago3DSChallenge from "../../components/payments/MercadoPago3DSChallenge";
import MercadoPagoCardForm from "../../components/payments/MercadoPagoCardForm";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import { useProjects } from "../../context/ProjectsContext";
import { useProjectSettingsDirty } from "../../components/dashboard/ProjectSettingsUnsavedContext";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import {
  billingPeriodOptions,
  quoteMonthlySelection,
  quotePeriodSelection,
} from "../../utils/billingPricing";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#D94B2D",
  dark: "var(--wp-panel-text-primary, #19242F)",
  muted: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.64)",
  subtle: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.045)",
  border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
};

const SERVICE_PRESENTATION = {
  landing: {
    icon: LanguageRoundedIcon,
    eyebrow: "Presencia online",
    benefit: "Mostrá tu negocio, recibí consultas y mantené una web profesional siempre visible.",
  },
  products: {
    icon: Inventory2RoundedIcon,
    eyebrow: "Catálogo",
    benefit: "Publicá productos o servicios con imágenes, precios, categorías y datos adicionales.",
  },
  orders: {
    icon: ShoppingCartRoundedIcon,
    eyebrow: "Ventas",
    benefit: "Recibí pedidos y seguí cada operación desde un flujo simple para tu equipo.",
  },
  payments: {
    icon: PaymentsRoundedIcon,
    eyebrow: "Cobros online",
    benefit: "Aceptá pagos con el Mercado Pago propio de tu comercio, sin mezclar cuentas.",
  },
  crm: {
    icon: PeopleAltRoundedIcon,
    eyebrow: "Relaciones",
    benefit: "Centralizá clientes, contactos e historial para que la relación comercial no dependa de la memoria.",
  },
  memberships: {
    icon: CardMembershipRoundedIcon,
    eyebrow: "Ingresos recurrentes",
    benefit: "Gestioná membresías, vigencias y renovaciones sobre el mismo CRM.",
  },
  system: {
    icon: BusinessCenterRoundedIcon,
    eyebrow: "Gestión",
    benefit: "Ordená movimientos, tareas y procesos internos desde un solo lugar.",
  },
  stats: {
    icon: AnalyticsRoundedIcon,
    eyebrow: "Indicadores",
    benefit: "Entendé la actividad del negocio con métricas fáciles de leer y usar.",
  },
  webpush: {
    icon: NotificationsActiveRoundedIcon,
    eyebrow: "Notificaciones",
    benefit: "Recibí alertas y enviá novedades a las personas suscriptas a tu web.",
  },
  assist: {
    icon: AutoAwesomeRoundedIcon,
    eyebrow: "Asistencia inteligente",
    benefit: "Base para cargar y operar WebProfit con lenguaje natural, voz y automatizaciones confirmables.",
  },
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "A confirmar";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "A confirmar";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmail(value) {
  const email = normalizeEmail(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function statusLabel(status) {
  const labels = {
    trial: "Prueba gratis activa",
    pending: "Falta terminar el pago",
    active: "Activo",
    past_due: "Pago pendiente",
    paused: "Pausado",
    cancelled: "Cancelado",
    exempt: "Bonificado",
    authorized: "Autorizado",
    manual_pending: "Pendiente",
    pending_payment: "Pago pendiente",
    approved: "Pagado",
    expired: "Vencido",
    rejected: "Rechazado",
  };
  return labels[status] || status || "Sin activar";
}

function statusColor(status) {
  if (["active", "authorized", "approved", "exempt", "trial"].includes(status)) return "success";
  if (["pending", "pending_payment", "manual_pending", "past_due"].includes(status)) return "warning";
  if (["paused", "cancelled", "expired", "rejected"].includes(status)) return "error";
  return "default";
}

function uniqueCodes(values = []) {
  return [...new Set((Array.isArray(values) ? values : []).map(String).filter(Boolean))].sort();
}

function sameCodes(left = [], right = []) {
  const a = uniqueCodes(left);
  const b = uniqueCodes(right);
  return a.length === b.length && a.every((code, index) => code === b[index]);
}

function splitFixedAmount(total, codes = []) {
  const orderedCodes = uniqueCodes(codes);
  if (!orderedCodes.length) return {};
  const totalCents = Math.max(0, Math.round(Number(total || 0) * 100));
  const baseCents = Math.floor(totalCents / orderedCodes.length);
  let remainder = totalCents - (baseCents * orderedCodes.length);
  return orderedCodes.reduce((acc, code) => {
    const cents = baseCents + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    acc[code] = cents / 100;
    return acc;
  }, {});
}

function expandSelection(codes, services) {
  const byCode = new Map((services || []).map((service) => [service.code, service]));
  const selected = new Set();

  const add = (code, trail = []) => {
    if (!code || selected.has(code) || trail.includes(code)) return;
    const service = byCode.get(code);
    if (!service) return;
    for (const dependency of service.requiredServiceCodes || []) {
      add(dependency, [...trail, code]);
    }
    selected.add(code);
  };

  (codes || []).forEach((code) => add(code));
  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function removeServiceAndDependants(code, selectedCodes, services) {
  const selected = new Set(selectedCodes || []);
  selected.delete(code);

  let changed = true;
  while (changed) {
    changed = false;
    for (const service of services || []) {
      if (!selected.has(service.code)) continue;
      const missingDependency = (service.requiredServiceCodes || []).some(
        (dependency) => !selected.has(dependency),
      );
      if (missingDependency) {
        selected.delete(service.code);
        changed = true;
      }
    }
  }

  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function subscriptionServicesLabel(subscription) {
  const snapshotNames = (subscription?.serviceSnapshot || [])
    .map((service) => service?.name)
    .filter(Boolean);
  if (snapshotNames.length) return snapshotNames.join(" + ");
  const codes = subscription?.requestedServiceCodes || [];
  return codes.length ? codes.join(" + ") : subscription?.requestedPlanCode || "—";
}

function Step({ number, children, mobileLabel = "" }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .45, sm: 1 }} alignItems="center" sx={{ minWidth: 0, textAlign: { xs: "center", sm: "left" } }}>
      <Box
        sx={{
          width: 27,
          height: 27,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,.14)",
          fontSize: 13,
          fontWeight: 950,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,.82)", fontWeight: 800, lineHeight: 1.2 }}>
        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{mobileLabel || children}</Box>
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{children}</Box>
      </Typography>
    </Stack>
  );
}

function SummaryMetric({ icon, title, value, helper, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: { xs: 1.35, sm: 2.25 },
        borderRadius: 3,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "var(--wp-panel-surface, #fff)",
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 34, sm: 42 },
            height: { xs: 34, sm: 42 },
            borderRadius: 2.25,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(243,90,58,.10)",
            color: BRAND.orange,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: BRAND.muted,
              fontWeight: 900,
              letterSpacing: ".04em",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.15,
              color: BRAND.dark,
              fontSize: { xs: "1.08rem", md: "1.2rem" },
              lineHeight: 1.2,
              fontWeight: 950,
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: { xs: "none", sm: "block" },
              mt: 0.4,
              color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
              lineHeight: 1.35,
            }}
          >
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function MobileServiceTile({
  service,
  selected,
  activeNow,
  selectable,
  presentation,
  requiredNames,
  requiredBy,
  disabled,
  paymentNeedsAttention,
  ownerLocked = false,
  onToggle,
}) {
  const ServiceIcon = presentation.icon || WorkspacePremiumRoundedIcon;
  const [expanded, setExpanded] = React.useState(false);
  const features = (service.features || []).filter(Boolean);
  const lockedByDependency = selected && requiredBy.length > 0;
  const controlDisabled = disabled || !selectable || lockedByDependency;
  const visibleStatus = ownerLocked && selected
    ? "Propietario"
    : !selectable
      ? "No disponible"
      : lockedByDependency
        ? "Dependencia"
        : activeNow && !selected
          ? "Se quitará"
          : null;
  const stateDescription = ownerLocked && selected
    ? "Módulo activado por el propietario desde Owner. No puede modificarse desde este panel."
    : !selectable
      ? "No disponible para nuevas altas."
      : lockedByDependency
        ? `Seleccionado como dependencia de ${requiredBy.join(", ")}. No se puede desactivar mientras esas capacidades sigan seleccionadas.`
        : activeNow && !selected
          ? "No seleccionado. Se quitará cuando se apliquen los cambios."
          : selected
            ? activeNow
              ? "Seleccionado. Ya forma parte del plan actual."
              : "Seleccionado. Se agregará cuando se apliquen los cambios."
            : "No seleccionado.";
  const globalDisabledDescription = controlDisabled && !lockedByDependency && selectable
    ? ownerLocked
      ? " Módulos activados por el propietario: la selección está bloqueada."
      : paymentNeedsAttention
        ? " La edición está bloqueada hasta regularizar el pago."
        : " La selección no se puede modificar ahora."
    : "";

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        overflow: "hidden",
        borderRadius: 2.75,
        border: selected ? `2px solid ${BRAND.orange}` : `1px solid ${BRAND.border}`,
        bgcolor: selected ? "rgba(243,90,58,.035)" : "var(--wp-panel-surface, #fff)",
        boxShadow: selected ? "0 7px 20px rgba(243,90,58,.10)" : "0 4px 14px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.045)",
        opacity: !selectable && !activeNow ? 0.68 : 1,
        transition: "border-color .16s ease, background-color .16s ease, box-shadow .16s ease",
      }}
    >
      <Box sx={{ position: "relative", minWidth: 0 }}>
        <ButtonBase
          role="checkbox"
          aria-checked={selected}
          aria-disabled={controlDisabled}
          aria-label={`${service.name}. ${money(service.monthlyPrice, service.currency)} por mes. ${stateDescription}${globalDisabledDescription}`}
          disableRipple={controlDisabled}
          onClick={() => {
            if (controlDisabled) return;
            onToggle(!selected);
          }}
          sx={{
            width: "100%",
            minHeight: 128,
            p: 1.2,
            pr: 4.8,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "space-between",
            gap: 0.85,
            textAlign: "left",
            borderRadius: 2.5,
            cursor: controlDisabled ? "default" : "pointer",
            "&.Mui-focusVisible": {
              outline: `3px solid rgba(243,90,58,.38)`,
              outlineOffset: -3,
            },
            "&:hover": controlDisabled
              ? undefined
              : { bgcolor: selected ? "rgba(243,90,58,.055)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: selected ? "rgba(243,90,58,.12)" : BRAND.subtle,
                color: selected ? BRAND.orange : BRAND.dark,
                flexShrink: 0,
              }}
            >
              <ServiceIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                border: selected ? "none" : `2px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.24)`,
                bgcolor: selected ? (lockedByDependency ? "rgba(7,90,121,.12)" : "rgba(243,90,58,.12)") : "transparent",
                color: lockedByDependency ? "#075A79" : BRAND.orange,
                flexShrink: 0,
              }}
            >
              {selected ? (lockedByDependency ? <LockRoundedIcon sx={{ fontSize: 15 }} /> : <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />) : null}
            </Box>
          </Stack>

          <Typography
            sx={{
              color: BRAND.dark,
              fontWeight: 950,
              fontSize: { xs: ".91rem", sm: ".96rem" },
              lineHeight: 1.18,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
              minHeight: "2.25em",
            }}
          >
            {service.name}
          </Typography>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05, fontSize: { xs: ".96rem", sm: "1rem" } }}>
              {money(service.monthlyPrice, service.currency)}
            </Typography>
            <Stack direction="row" spacing={0.55} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35, minHeight: 22 }}>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 750 }}>
                /mes{ownerLocked ? " · parte del precio promocional" : ""}
              </Typography>
              {visibleStatus ? (
                <Chip
                  size="small"
                  label={visibleStatus}
                  icon={lockedByDependency ? <LockRoundedIcon /> : undefined}
                  sx={{
                    height: 21,
                    maxWidth: "100%",
                    fontSize: 9.5,
                    fontWeight: 950,
                    bgcolor: !selectable
                      ? "rgba(211,47,47,.08)"
                      : lockedByDependency
                        ? "rgba(2,136,209,.10)"
                        : "rgba(211,47,47,.08)",
                    color: !selectable || activeNow && !selected ? "#B3261E" : "#075A79",
                    "& .MuiChip-icon": { fontSize: 13, color: "inherit" },
                    "& .MuiChip-label": { px: 0.65, overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        </ButtonBase>

        <IconButton
          size="small"
          aria-label={`${expanded ? "Ocultar" : "Ver"} detalles de ${service.name}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          sx={{
            position: "absolute",
            right: 5,
            bottom: 5,
            width: 40,
            height: 40,
            color: BRAND.muted,
            bgcolor: "rgba(255,255,255,.82)",
            "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)", color: BRAND.dark },
            "&.Mui-focusVisible": { outline: `2px solid ${BRAND.orange}`, outlineOffset: 1 },
          }}
        >
          {expanded ? <ExpandLessRoundedIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.2, pb: 1.2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.4 }}>
            {presentation.benefit || service.description || "Herramientas principales de esta capacidad."}
          </Typography>
          {service.customPriceApplied ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: BRAND.orangeDark, fontWeight: 900 }}>
              Este proyecto tiene un precio especial para esta capacidad.
            </Typography>
          ) : null}
          {requiredNames.length ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: BRAND.muted, fontWeight: 800 }}>
              Al elegirlo también se selecciona: {requiredNames.join(", ")}.
            </Typography>
          ) : null}
          {lockedByDependency ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#075A79", fontWeight: 850 }}>
              Dependencia de: {requiredBy.join(", ")}.
            </Typography>
          ) : null}
          <Box sx={{ mt: 0.9, display: "grid", gap: 0.55 }}>
            {(features.length ? features : ["Herramientas principales del módulo"]).map((feature) => (
              <Stack key={feature} direction="row" spacing={0.65} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <CheckCircleRoundedIcon sx={{ mt: 0.1, fontSize: 15, color: "success.main", flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: BRAND.dark, lineHeight: 1.35 }}>
                  {feature}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

function ServiceRow({
  service,
  selected,
  activeNow,
  selectable,
  presentation,
  requiredNames,
  requiredBy,
  disabled,
  paymentNeedsAttention,
  ownerLocked = false,
  onToggle,
}) {
  const ServiceIcon = presentation.icon || WorkspacePremiumRoundedIcon;
  const [expanded, setExpanded] = React.useState(false);
  const features = (service.features || []).filter(Boolean);
  const lockedByDependency = selected && requiredBy.length > 0;
  const controlDisabled = disabled || !selectable || lockedByDependency;

  const status = ownerLocked && selected
    ? "Propietario"
    : !selectable
      ? "No disponible"
      : lockedByDependency
        ? "Dependencia"
        : activeNow && selected
          ? "Incluido"
          : activeNow && !selected
            ? "Se quitará"
            : selected
              ? "Se agregará"
              : "Opcional";

  const statusSx = ownerLocked && selected
    ? { bgcolor: "rgba(46,125,50,.11)", color: "#2E7D32" }
    : !selectable
      ? { bgcolor: "rgba(211,47,47,.08)", color: "#B3261E" }
      : lockedByDependency
        ? { bgcolor: "rgba(2,136,209,.10)", color: "#075A79" }
        : activeNow && selected
          ? { bgcolor: "rgba(46,125,50,.11)", color: "#2E7D32" }
        : activeNow && !selected
          ? { bgcolor: "rgba(211,47,47,.09)", color: "#B3261E" }
          : selected
            ? { bgcolor: "rgba(243,90,58,.11)", color: BRAND.orangeDark }
            : { bgcolor: BRAND.subtle, color: BRAND.muted };

  return (
    <Box
      sx={{
        minWidth: 0,
        bgcolor: selected ? "rgba(243,90,58,.025)" : "var(--wp-panel-surface, #fff)",
        borderLeft: selected ? `4px solid ${BRAND.orange}` : "4px solid transparent",
        transition: "background-color .18s ease, border-color .18s ease",
        "&:not(:last-of-type)": { borderBottom: `1px solid ${BRAND.border}` },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "44px minmax(0,1fr) auto",
            md: "44px minmax(220px,1fr) minmax(120px,.55fr) 132px 92px",
          },
          gap: { xs: 1.15, md: 1.75 },
          alignItems: "center",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1.75 },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.25,
            display: "grid",
            placeItems: "center",
            bgcolor: selected ? "rgba(243,90,58,.11)" : BRAND.subtle,
            color: selected ? BRAND.orange : BRAND.dark,
            flexShrink: 0,
          }}
        >
          <ServiceIcon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{
                color: BRAND.dark,
                fontWeight: 950,
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              {service.name}
            </Typography>
            <Chip
              size="small"
              label={status}
              sx={{ height: 23, fontSize: 10.5, fontWeight: 950, ...statusSx }}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: BRAND.muted,
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: expanded ? "unset" : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {presentation.benefit || service.description}
          </Typography>

          {lockedByDependency ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.65, color: "#075A79", fontWeight: 850 }}
            >
              Se mantiene activo porque lo necesita: {requiredBy.join(", ")}.
            </Typography>
          ) : requiredNames.length ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.65, color: BRAND.muted, fontWeight: 750 }}
            >
              Al activarlo también se incluyen: {requiredNames.join(", ")}.
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            minWidth: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 850 }}>
            {presentation.eyebrow || "Capacidad WebProfit"}
          </Typography>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            sx={{
              display: "flex",
              mt: 0.15,
              minWidth: 0,
              px: 0,
              justifyContent: "flex-start",
              color: BRAND.dark,
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            {expanded ? "Ocultar detalles" : "Ver qué incluye"}
          </Button>
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "right" }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05 }}>
            {money(service.monthlyPrice, service.currency)}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted }}>
            {ownerLocked ? "parte del precio promocional" : "por mes"}
          </Typography>
          {!ownerLocked && service.customPriceApplied ? (
            <Typography variant="caption" sx={{ display: "block", color: BRAND.orange, fontWeight: 800 }}>
              Precio especial
            </Typography>
          ) : null}
        </Box>

        <Stack alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
          <Switch
            checked={selected}
            disabled={controlDisabled}
            onChange={(event) => onToggle(event.target.checked)}
            inputProps={{ "aria-label": `${selected ? "Desactivar" : "Activar"} ${service.name}` }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND.orange },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND.orange },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: { xs: "none", md: "block" },
              color: controlDisabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.38)" : BRAND.muted,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.15,
            }}
          >
            {ownerLocked && selected
              ? "Propietario"
              : paymentNeedsAttention
                ? "Bloqueado"
                : !selectable
                  ? "No disponible"
                  : lockedByDependency
                    ? "Dependencia"
                    : selected
                      ? "Activado"
                      : activeNow
                        ? "Se quitará"
                        : "Activar"}
          </Typography>
        </Stack>

        <Box
          sx={{
            gridColumn: { xs: "2 / -1", md: "auto" },
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: -0.35,
          }}
        >
          <Box>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05 }}>
              {money(service.monthlyPrice, service.currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>
              {ownerLocked ? "parte del precio promocional" : "por mes"}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            sx={{ color: BRAND.dark, textTransform: "none", fontWeight: 850 }}
          >
            {expanded ? "Cerrar" : "Detalles"}
          </Button>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mx: { xs: 1.5 },
            ml: { md: "60px" },
            mr: { md: 2 },
            mb: 1.6,
            p: 1.5,
            borderRadius: 2.25,
            bgcolor: BRAND.subtle,
          }}
        >
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
            QUÉ INCLUYE
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
              gap: 0.75,
            }}
          >
            {(features.length ? features : ["Herramientas principales del módulo"]).map((feature) => (
              <Stack key={feature} direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <CheckCircleRoundedIcon sx={{ mt: 0.1, fontSize: 16, color: "success.main", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: BRAND.dark, lineHeight: 1.35 }}>
                  {feature}
                </Typography>
              </Stack>
            ))}
          </Box>
          {!selectable ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "error.main", fontWeight: 800 }}>
              Esta capacidad no está disponible para nuevas altas.
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}


function BillingPaymentChoice({
  paymentMode,
  amount,
  currency,
  publicKey,
  embeddedCardConfigured,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  fallbackDisabled,
  fallbackLabel,
  onFallback,
  working,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
}) {
  const [channel, setChannel] = React.useState(
    embeddedCardConfigured ? "card" : "mercadopago",
  );

  React.useEffect(() => {
    if (!embeddedCardConfigured && channel === "card") {
      setChannel("mercadopago");
    }
  }, [channel, embeddedCardConfigured]);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
        DÓNDE QUERÉS PAGAR
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={channel}
        onChange={(_event, value) => value && setChannel(value)}
        sx={{
          mt: 1,
          "& .MuiToggleButton-root": {
            minWidth: 0,
            py: 1,
            px: 1,
            gap: 0.65,
            textTransform: "none",
            fontSize: ".82rem",
            fontWeight: 900,
          },
        }}
      >
        <ToggleButton value="card" disabled={!embeddedCardConfigured}>
          <CreditCardRoundedIcon sx={{ fontSize: 18 }} />
          Tarjeta acá
        </ToggleButton>
        <ToggleButton value="mercadopago">
          <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />
          Mercado Pago
        </ToggleButton>
      </ToggleButtonGroup>

      <Collapse in={channel === "card"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
          }}
        >
          {!embeddedCardConfigured ? (
            <Alert severity="warning">
              Falta configurar la Public Key de Mercado Pago para mostrar la tarjeta dentro del panel.
            </Alert>
          ) : threeDSChallenge ? (
            <MercadoPago3DSChallenge
              externalResourceUrl={threeDSChallenge.externalResourceUrl}
              creq={threeDSChallenge.creq}
              onComplete={onThreeDSComplete}
            />
          ) : (
            <MercadoPagoCardForm
              key={`${paymentMode}-${amount}-${publicKey}`}
              publicKey={publicKey}
              amount={amount}
              currency={currency}
              buyer={{ email: payerEmail }}
              processing={working}
              disabled={cardDisabled}
              compact
              hideHeader
              submitLabel={
                paymentMode === "recurring"
                  ? recurringActive
                    ? "Actualizar esta tarjeta"
                    : "Suscribirme con esta tarjeta"
                  : `Pagar ${money(amount, currency)}`
              }
              onSubmit={onCardSubmit}
              onError={() => {}}
            />
          )}
          {cardError ? (
            <Alert severity="error" sx={{ mt: 1.25 }}>
              {cardError}
            </Alert>
          ) : null}
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.1, color: BRAND.muted, lineHeight: 1.45 }}
          >
            {paymentMode === "recurring"
              ? "La tarjeta queda asociada al débito mensual. El correo del formulario puede ser distinto al de WebProfit."
              : "Es un único cobro por el período. WebProfit no guarda los datos de la tarjeta."}
          </Typography>
        </Box>
      </Collapse>

      <Collapse in={channel === "mercadopago"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.6,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: BRAND.subtle,
          }}
        >
          {paymentMode === "recurring" ? (
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Correo de la cuenta de Mercado Pago"
              value={payerEmail}
              onChange={(event) => onPayerEmailChange(event.target.value)}
              error={payerEmailRequired && payerEmail.length > 0 && !isValidEmail(payerEmail)}
              helperText={
                payerEmailRequired && !payerEmail
                  ? "Ingresá el correo que autorizará el débito."
                  : "Puede ser distinto al correo de WebProfit."
              }
            />
          ) : null}
          <Typography
            variant="body2"
            sx={{ mt: paymentMode === "recurring" ? 1.25 : 0, color: BRAND.muted, lineHeight: 1.5 }}
          >
            {paymentMode === "recurring"
              ? "Te llevamos a Mercado Pago para autorizar la suscripción con tu cuenta o una tarjeta disponible."
              : "Te llevamos a Mercado Pago para pagar con saldo, tarjeta u otro medio habilitado."}
          </Typography>
          <Button
            fullWidth
            disabled={fallbackDisabled}
            onClick={onFallback}
            variant="contained"
            startIcon={
              working ? (
                <WebProfitSpinner size={18} color="inherit" />
              ) : (
                <AccountBalanceWalletRoundedIcon />
              )
            }
            sx={{
              mt: 1.5,
              minHeight: 46,
              bgcolor: "#009EE3",
              borderRadius: 2.25,
              textTransform: "none",
              fontWeight: 950,
              "&:hover": { bgcolor: "#008CC9" },
            }}
          >
            {fallbackLabel}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

function CheckoutSummary({
  selectedServices,
  selectedAmount,
  monthlyPricing,
  periodPricing,
  periodMonths,
  periodOptions,
  onPeriodMonthsChange,
  currency,
  billing,
  billingAccess,
  paymentNeedsAttention,
  paymentMode,
  onPaymentModeChange,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  recurringLocked,
  oneTimeLocked,
  selectionChanged,
  actionLabel,
  primaryDisabled,
  primaryAction,
  working,
  mercadoPagoPublicKey,
  embeddedCardConfigured,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
}) {
  const paymentSuspended = billingAccess?.reason === "billing_suspended";
  const displayAmount = paymentMode === "one_time"
    ? Number(periodPricing?.total || selectedAmount || 0)
    : Number(selectedAmount || 0);
  const selectedPeriodLabel = periodMonths === 1 ? "1 mes" : `${periodMonths} meses`;

  return (
    <Paper
      elevation={0}
      sx={{
        position: { lg: "sticky" },
        top: { lg: 20 },
        p: { xs: 2.25, sm: 2.75 },
        borderRadius: 3.5,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 18px 46px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.09)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          mx: { xs: -2.25, sm: -2.75 },
          mt: { xs: -2.25, sm: -2.75 },
          mb: 2.4,
          px: { xs: 2.25, sm: 2.75 },
          py: 2.1,
          bgcolor: BRAND.dark,
          color: "#fff",
        }}
      >
        <Typography variant="overline" sx={{ color: "rgba(255,255,255,.62)", fontWeight: 900 }}>
          RESUMEN DE TU PLAN
        </Typography>
        <Stack direction="row" spacing={0.7} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography
            sx={{
              fontSize: { xs: "2rem", sm: "2.25rem" },
              lineHeight: 1.05,
              fontWeight: 950,
              letterSpacing: "-.045em",
              overflowWrap: "anywhere",
            }}
          >
            {money(displayAmount, currency)}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,.68)", fontWeight: 700 }}>
            {paymentMode === "one_time" ? `por ${selectedPeriodLabel}` : "/ mes"}
          </Typography>
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
            {selectedServices.length} {selectedServices.length === 1 ? "módulo incluido" : "módulos incluidos"}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted }}>
            {monthlyPricing?.commercialOverride?.active
              ? "Precio promocional administrado por el propietario"
              : paymentMode === "one_time"
                ? "Un pago, sin renovación automática"
                : "Se renueva automáticamente cada mes"}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={billingAccess.isGrace
            ? "Pago pendiente"
            : paymentSuspended
              ? "Suspendido por pago"
              : billingAccess.isLandingOnly
                ? "Acceso limitado"
              : selectionChanged
                ? "Sin confirmar"
                : statusLabel(billing.status)}
          color={billingAccess.isLandingOnly || paymentSuspended
            ? "error"
            : billingAccess.isGrace || selectionChanged
              ? "warning"
              : statusColor(billing.status)}
          sx={{ maxWidth: 150, fontWeight: 900 }}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
          CÓMO QUERÉS PAGAR
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={paymentMode}
          onChange={(_event, value) => value && onPaymentModeChange(value)}
          sx={{ mt: 1, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 900, py: 1 } }}
        >
          <ToggleButton value="recurring" disabled={recurringLocked}>
            Automático
          </ToggleButton>
          <ToggleButton value="one_time" disabled={oneTimeLocked}>
            Solo este período
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BRAND.muted, lineHeight: 1.45 }}>
          {paymentMode === "one_time"
            ? "Mercado Pago cobra una sola vez. Al vencer el período tendrás días de gracia para volver a pagar."
            : "Mercado Pago autoriza una tarjeta compatible y vuelve a cobrar cada mes automáticamente."}
        </Typography>
        {paymentMode === "one_time" ? (
          <Box sx={{ mt: 1.35 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 0.75, color: BRAND.dark, fontWeight: 950 }}>
              ELEGÍ CUÁNTO TIEMPO QUERÉS CUBRIR
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={periodMonths}
              onChange={(_event, value) => value && onPeriodMonthsChange(value)}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2,minmax(0,1fr))",
                gap: 0.75,
                "& .MuiToggleButtonGroup-grouped": {
                  m: "0 !important",
                  border: `1px solid ${BRAND.border} !important`,
                  borderRadius: "10px !important",
                  textTransform: "none",
                  fontWeight: 900,
                  minWidth: 0,
                },
              }}
            >
              {periodOptions.map((option) => (
                <ToggleButton key={option.months} value={option.months}>
                  <Stack spacing={0.1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box component="span">{option.months === 1 ? "1 mes" : `${option.months} meses`}</Box>
                    {option.discountPercent > 0 ? (
                      <Box component="span" sx={{ fontSize: 10.5, color: "success.main", fontWeight: 950 }}>
                        -{option.discountPercent}%
                      </Box>
                    ) : null}
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {periodMonths > 1 ? (
              <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BRAND.muted, lineHeight: 1.4 }}>
                Equivale a {money(periodPricing?.equivalentMonthly, currency)} por mes y no se renueva solo.
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {recurringLocked ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: "warning.dark", fontWeight: 800 }}>
            El período actual ya está pagado. El débito automático podrá activarse cuando se acerque su vencimiento.
          </Typography>
        ) : null}
        {oneTimeLocked ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: "warning.dark", fontWeight: 800 }}>
            Ya existe una suscripción automática activa; no generamos otro cobro para evitar duplicados.
          </Typography>
        ) : null}

      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.05} sx={{ maxHeight: 210, overflowY: "auto", pr: 0.5 }}>
        {selectedServices.map((service) => (
          <Stack key={service.code} direction="row" justifyContent="space-between" spacing={1.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <CheckCircleRoundedIcon sx={{ mt: 0.15, fontSize: 17, color: "success.main", flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 750, overflowWrap: "anywhere" }}>
                {service.name}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 900, flexShrink: 0 }}>
              {money(service.monthlyPrice, service.currency)}
            </Typography>
          </Stack>
        ))}
        {!selectedServices.length ? (
          <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: BRAND.subtle }}>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              Elegí al menos una capacidad para armar tu WebProfit.
            </Typography>
          </Box>
        ) : null}
      </Stack>

      {(Number(monthlyPricing?.savings || 0) > 0 || (paymentMode === "one_time" && Number(periodPricing?.discountAmount || 0) > 0)) ? (
        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2.25, bgcolor: "rgba(46,125,50,.07)", border: "1px solid rgba(46,125,50,.16)" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <SavingsRoundedIcon sx={{ fontSize: 18, color: "success.main" }} />
            <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 950 }}>
              Ahorro automático
            </Typography>
          </Stack>
          {Number(monthlyPricing?.savings || 0) > 0 ? (
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.8 }}>
              <Typography variant="caption" sx={{ color: BRAND.muted }}>Por combinar capacidades</Typography>
              <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 950 }}>
                -{money(monthlyPricing.savings, currency)} / mes
              </Typography>
            </Stack>
          ) : null}
          {paymentMode === "one_time" && Number(periodPricing?.discountAmount || 0) > 0 ? (
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.45 }}>
              <Typography variant="caption" sx={{ color: BRAND.muted }}>Por pagar {selectedPeriodLabel}</Typography>
              <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 950 }}>
                -{money(periodPricing.discountAmount, currency)}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <BillingPaymentChoice
        paymentMode={paymentMode}
        amount={displayAmount}
        currency={currency}
        publicKey={mercadoPagoPublicKey}
        embeddedCardConfigured={embeddedCardConfigured}
        payerEmail={payerEmail}
        onPayerEmailChange={onPayerEmailChange}
        payerEmailRequired={payerEmailRequired}
        fallbackDisabled={primaryDisabled}
        fallbackLabel={actionLabel}
        onFallback={primaryAction}
        working={working}
        cardDisabled={cardDisabled}
        onCardSubmit={onCardSubmit}
        cardError={cardError}
        threeDSChallenge={threeDSChallenge}
        onThreeDSComplete={onThreeDSComplete}
        recurringActive={recurringActive}
      />

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.4, textAlign: "center", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
      >
        Los datos de tarjeta los procesa Mercado Pago. Este cobro no se mezcla con los cobros de tu tienda.
      </Typography>
    </Paper>
  );
}

export default function PlanBillingPage({ embedded = false }) {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadProjects, setActiveProject } = useProjects();
  const [data, setData] = React.useState(null);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [paymentMode, setPaymentMode] = React.useState("recurring");
  const [periodMonths, setPeriodMonths] = React.useState(1);
  const [payerEmail, setPayerEmail] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [cardError, setCardError] = React.useState("");
  const [threeDSChallenge, setThreeDSChallenge] = React.useState(null);
  const returnHandled = React.useRef(false);
  const paymentModeInitialized = React.useRef(false);
  const periodMonthsInitialized = React.useRef(false);
  const payerEmailInitialized = React.useRef(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await planBillingApi.getProject(projectId);
      setData(response);
      if (response.project) setActiveProject(response.project);
    } catch (err) {
      setError(err.message || "No se pudo cargar tu plan");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    paymentModeInitialized.current = false;
    periodMonthsInitialized.current = false;
    payerEmailInitialized.current = false;
    returnHandled.current = false;
    load();
  }, [load]);

  React.useEffect(() => {
    const services = data?.services || [];
    const pending = data?.billing?.pendingServiceCodes || [];
    const current = data?.billing?.serviceCodes || [];
    setSelectedCodes(expandSelection(pending.length ? pending : current, services));
    if (!paymentModeInitialized.current && data?.billing) {
      setPaymentMode(
        data.billing.paymentMode === "one_time" ? "one_time" : "recurring",
      );
      paymentModeInitialized.current = true;
    }
    if (!periodMonthsInitialized.current) {
      const pendingCheckout = (data?.checkouts || []).find((checkout) =>
        checkout?.status === "pending_payment" &&
        (!data?.billing?.pendingCheckoutId ||
          String(checkout.id || checkout._id) === String(data.billing.pendingCheckoutId)),
      );
      setPeriodMonths(Number(pendingCheckout?.periodMonths || 1));
      periodMonthsInitialized.current = true;
    }
    if (!payerEmailInitialized.current && data?.billing) {
      const lastSubscriptionEmail = (data.subscriptions || []).find(
        (subscription) => subscription?.payerEmail,
      )?.payerEmail;
      setPayerEmail(
        normalizeEmail(data.billing.payerEmail || lastSubscriptionEmail || ""),
      );
      payerEmailInitialized.current = true;
    }
  }, [data]);

  const billingAreaDirty = React.useMemo(() => {
    if (!data?.billing || loading) return false;
    const services = data.services || [];
    const pending = data.billing.pendingServiceCodes || [];
    const current = data.billing.serviceCodes || [];
    const baselineCodes = expandSelection(pending.length ? pending : current, services).map(String).sort();
    const currentCodes = selectedCodes.map(String).sort();
    const baselineMode = data.billing.paymentMode === "one_time" ? "one_time" : "recurring";
    const lastSubscriptionEmail = (data.subscriptions || []).find((subscription) => subscription?.payerEmail)?.payerEmail;
    const baselineEmail = normalizeEmail(data.billing.payerEmail || lastSubscriptionEmail || "");
    return JSON.stringify(currentCodes) !== JSON.stringify(baselineCodes)
      || paymentMode !== baselineMode
      || (paymentMode === "one_time" && periodMonths !== 1)
      || normalizeEmail(payerEmail) !== baselineEmail;
  }, [data, loading, selectedCodes, paymentMode, periodMonths, payerEmail]);
  useProjectSettingsDirty("billing", billingAreaDirty);

  React.useEffect(() => {
    const paymentId =
      searchParams.get("payment_id") || searchParams.get("collection_id") || "";
    const returned =
      searchParams.get("subscription") ||
      searchParams.get("preapproval_id") ||
      searchParams.get("billing_payment") ||
      searchParams.get("status") ||
      paymentId;
    if (!returned || returnHandled.current) return;
    returnHandled.current = true;

    setWorking(true);
    planBillingApi
      .refresh(projectId, paymentId ? { paymentId } : {})
      .then(async () => {
        setMessage("Listo. Revisamos Mercado Pago y actualizamos tu plan.");
        await Promise.all([load(), loadProjects()]);
        const next = new URLSearchParams(searchParams);
        [
          "subscription",
          "preapproval_id",
          "billing_payment",
          "payment_id",
          "collection_id",
          "collection_status",
          "external_reference",
          "payment_type",
          "merchant_order_id",
          "preference_id",
          "site_id",
          "processing_mode",
          "merchant_account_id",
          "status",
        ].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      })
      .catch((err) => setError(err.message || "No se pudo verificar el pago"))
      .finally(() => setWorking(false));
  }, [load, loadProjects, projectId, searchParams, setSearchParams]);

  const run = async (operation, successMessage) => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const response = await operation();
      setMessage(response?.message || successMessage);
      await Promise.all([load(), loadProjects()]);
      return response;
    } catch (err) {
      setError(err.message || "No se pudo completar la operación");
      return null;
    } finally {
      setWorking(false);
    }
  };

  const billing = data?.billing || {};
  const ownerManagedPricing = billing?.ownerManagedPricing || null;
  const ownerFullSystem = Boolean(
    ownerManagedPricing?.linked
      && ownerManagedPricing?.accessScope === "full_system"
      && ownerManagedPricing?.pricingMode === "fixed_total",
  );
  const billingAccess = getProjectBillingAccess({
    ...(data?.project || {}),
    billing,
  });
  const trialActive = billingAccess.isTrial && !billingAccess.trialExpired;
  const paymentSuspended = billingAccess.reason === "billing_suspended";
  const paymentNeedsAttention = billingAccess.needsPaymentAction;
  const services = data?.services || [];
  const pricingPolicy = data?.pricingPolicy || {};
  const periodOptions = billingPeriodOptions(pricingPolicy);
  const serviceByCode = new Map(services.map((service) => [service.code, service]));
  const selectedServices = services.filter((service) => selectedCodes.includes(service.code));
  const catalogMonthlyPricing = quoteMonthlySelection(selectedServices, pricingPolicy);
  const selectedAmount = ownerFullSystem
    ? Number(ownerManagedPricing?.fixedMonthlyAmount || billing.monthlyAmount || 0)
    : catalogMonthlyPricing.total;
  const ownerAllocationRows = Array.isArray(ownerManagedPricing?.moduleAllocation)
    ? ownerManagedPricing.moduleAllocation
    : [];
  const promotionalAllocation = ownerFullSystem
    ? (ownerAllocationRows.length
        ? ownerAllocationRows.reduce((acc, row) => {
            if (row?.code) acc[String(row.code)] = Number(row.amount || 0);
            return acc;
          }, {})
        : splitFixedAmount(selectedAmount, selectedCodes))
    : {};
  const displayedSelectedServices = ownerFullSystem
    ? selectedServices.map((service) => ({
        ...service,
        monthlyPrice: Number(promotionalAllocation[service.code] || 0),
        customPriceApplied: false,
      }))
    : selectedServices;
  const monthlyPricing = ownerFullSystem
    ? {
        ...catalogMonthlyPricing,
        subtotal: selectedAmount,
        total: selectedAmount,
        savings: 0,
        commercialOverride: { active: true, mode: "fixed_total" },
      }
    : catalogMonthlyPricing;
  const periodPricing = quotePeriodSelection(monthlyPricing, periodMonths, pricingPolicy);
  const checkoutAmount = paymentMode === "one_time" ? periodPricing.total : selectedAmount;
  const currency = selectedServices[0]?.currency || billing.currency || "ARS";
  const serviceOptions = services.map((service) => {
    const selected = selectedCodes.includes(service.code);
    const activeNow = (billing.serviceCodes || []).includes(service.code);
    const selectable = service.active !== false || activeNow;
    const presentation = SERVICE_PRESENTATION[service.code] || {};
    const requiredNames = (service.requiredServiceCodes || [])
      .map((code) => serviceByCode.get(code)?.name)
      .filter(Boolean);
    const requiredBy = services
      .filter(
        (candidate) =>
          selectedCodes.includes(candidate.code) &&
          (candidate.requiredServiceCodes || []).includes(service.code),
      )
      .map((candidate) => candidate.name);

    const displayService = ownerFullSystem && selected
      ? {
          ...service,
          monthlyPrice: Number(promotionalAllocation[service.code] || 0),
          customPriceApplied: false,
        }
      : service;

    return {
      service: displayService,
      selected,
      activeNow,
      selectable,
      presentation,
      requiredNames,
      requiredBy,
      ownerLocked: ownerFullSystem,
    };
  });
  const currentCodes = billing.pendingServiceCodes?.length
    ? billing.pendingServiceCodes
    : billing.serviceCodes || [];
  const selectionChanged = !sameCodes(selectedCodes, currentCodes);
  const recurringPendingUrl =
    billing.pendingSubscriptionUrl ||
    (billing.status === "pending" ? billing.subscriptionUrl : "");
  const periodPendingUrl = billing.pendingCheckoutUrl || "";
  const pendingCheckoutUrl =
    paymentMode === "one_time" ? periodPendingUrl : recurringPendingUrl;
  const pendingRecord = paymentMode === "one_time"
    ? (data?.checkouts || []).find(
        (checkout) =>
          checkout.status === "pending_payment" &&
          (!billing.pendingCheckoutId ||
            String(checkout.id || checkout._id) === String(billing.pendingCheckoutId)),
      )
    : (data?.subscriptions || []).find((subscription) => {
        if (subscription.status !== "pending") return false;
        const providerId = String(subscription.providerSubscriptionId || "");
        return !billing.pendingSubscriptionId || providerId === billing.pendingSubscriptionId;
      });
  const pendingPriceChanged = Boolean(
    pendingCheckoutUrl &&
      pendingRecord &&
      (Math.abs(Number(pendingRecord.amount || 0) - checkoutAmount) >= 0.01 ||
        String(pendingRecord.currency || "ARS").toUpperCase() !==
          String(currency || "ARS").toUpperCase()),
  );
  const pendingPayerEmailChanged = Boolean(
    paymentMode === "recurring" &&
      pendingCheckoutUrl &&
      pendingRecord?.payerEmail &&
      normalizeEmail(pendingRecord.payerEmail) !== normalizeEmail(payerEmail),
  );
  const priceNeedsConfirmation = Boolean(
    billing.pricingOutdated || pendingPriceChanged || pendingPayerEmailChanged,
  );
  const canControlProvider =
    billing.paymentMode === "recurring" &&
    billing.provider === "mercadopago" &&
    Boolean(billing.subscriptionId);
  const canSubmit =
    Boolean(data?.canManage) &&
    Boolean(data?.mercadoPagoConfigured) &&
    selectedCodes.length > 0;
  const featureRequested = searchParams.get("feature");
  const isActive = ["active", "exempt"].includes(billing.status);
  const currentPeriodEnd = billing.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd)
    : null;
  const hasFuturePaidPeriod = Boolean(
    billing.paymentMode === "one_time" &&
      billing.status === "active" &&
      currentPeriodEnd &&
      !Number.isNaN(currentPeriodEnd.getTime()) &&
      currentPeriodEnd > new Date(),
  );
  const oneTimeLocked = Boolean(
    billing.paymentMode === "recurring" &&
      billing.status === "active" &&
      billing.subscriptionId,
  );
  const recurringLocked = hasFuturePaidPeriod;
  const paymentModeChanged = Boolean(
    isActive &&
      ["recurring", "one_time"].includes(billing.paymentMode) &&
      billing.paymentMode !== paymentMode,
  );
  const recurringNeedsAuthorization =
    paymentMode === "recurring" &&
    (!isActive || !billing.isRecurring || paymentModeChanged);
  const payerEmailRequired = Boolean(
    paymentMode === "recurring" &&
      (recurringNeedsAuthorization || paymentNeedsAttention || pendingCheckoutUrl),
  );
  const payerEmailReady = !payerEmailRequired || isValidEmail(payerEmail);
  const hasCheckoutAction =
    paymentMode === "one_time" ||
    recurringNeedsAuthorization ||
    selectionChanged ||
    priceNeedsConfirmation ||
    Boolean(pendingCheckoutUrl);
  const effectivePaymentMode =
    isActive || paymentNeedsAttention
      ? billing.paymentMode === "one_time"
        ? "one_time"
        : "recurring"
      : paymentMode;
  const billingHistory = [
    ...(data?.subscriptions || []).map((item) => ({
      ...item,
      historyType: "recurring",
    })),
    ...(data?.checkouts || []).map((item) => ({
      ...item,
      historyType: "one_time",
    })),
  ].sort(
    (left, right) =>
      new Date(right.createdAt || 0).getTime() -
      new Date(left.createdAt || 0).getTime(),
  );


  const toggleService = (service, checked) => {
    if (checked) {
      const nextCodes = expandSelection([...selectedCodes, service.code], services);
      const automaticallyAdded = nextCodes.filter(
        (code) => !selectedCodes.includes(code) && code !== service.code,
      );
      setSelectedCodes(nextCodes);
      if (automaticallyAdded.length) {
        const names = automaticallyAdded
          .map((code) => serviceByCode.get(code)?.name)
          .filter(Boolean)
          .join(", ");
        setMessage(`${service.name} necesita ${names}. WebProfit los agregó automáticamente.`);
      }
      return;
    }

    const requiredBy = services.filter(
      (candidate) =>
        selectedCodes.includes(candidate.code) &&
        (candidate.requiredServiceCodes || []).includes(service.code),
    );

    if (requiredBy.length) {
      setMessage(
        `${service.name} no se puede desactivar mientras esté activo ${requiredBy
          .map((candidate) => candidate.name)
          .join(", ")}.`,
      );
      return;
    }

    setSelectedCodes((current) => removeServiceAndDependants(service.code, current, services));
  };

  const changePaymentMode = (nextMode) => {
    if (nextMode === "one_time" && oneTimeLocked) return;
    if (nextMode === "recurring" && recurringLocked) return;
    setPaymentMode(nextMode);
    setCardError("");
    setThreeDSChallenge(null);
    setMessage(
      nextMode === "one_time"
        ? "Elegiste pagar solamente este período. No habrá renovación automática."
        : "Elegiste débito automático mensual.",
    );
  };

  const saveSelection = async () => {
    if (pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation) {
      window.location.assign(pendingCheckoutUrl);
      return;
    }
    const response = await run(
      () =>
        paymentMode === "one_time"
          ? planBillingApi.checkout(projectId, {
              serviceCodes: selectedCodes,
              paymentMode: "one_time",
              periodMonths,
            })
          : planBillingApi.subscribe(projectId, {
              serviceCodes: selectedCodes,
              payerEmail: normalizeEmail(payerEmail),
            }),
      paymentMode === "one_time"
        ? "Mercado Pago está listo para cobrar este período."
        : isActive
          ? "Tu plan quedó actualizado."
          : "Tu plan está listo para autorizarse en Mercado Pago.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };


  const payWithEmbeddedCard = async ({ formData }) => {
    try {
      setWorking(true);
      setError("");
      setCardError("");
      setMessage("");
      const response = await planBillingApi.payWithCard(projectId, {
        paymentMode,
        serviceCodes: selectedCodes,
        periodMonths,
        formData,
      });

      const formEmail = normalizeEmail(formData?.payer?.email || "");
      if (formEmail) setPayerEmail(formEmail);

      if (response?.payment?.threeDS?.required) {
        setThreeDSChallenge({
          ...response.payment.threeDS,
          paymentId: response.payment.paymentId,
        });
        setMessage("El banco necesita una verificación adicional para terminar el pago.");
        return response;
      }

      setThreeDSChallenge(null);
      setMessage(
        response?.message ||
          (paymentMode === "recurring"
            ? "El débito automático quedó autorizado."
            : "El pago fue procesado."),
      );
      await Promise.all([load(), loadProjects()]);
      return response;
    } catch (err) {
      const nextMessage = err?.message || "No se pudo procesar la tarjeta";
      setCardError(nextMessage);
      setError(nextMessage);
      return null;
    } finally {
      setWorking(false);
    }
  };

  const completeThreeDSChallenge = async () => {
    const paymentId = threeDSChallenge?.paymentId;
    if (!paymentId) return;
    try {
      setWorking(true);
      setCardError("");
      setError("");
      await planBillingApi.refresh(projectId, { paymentId });
      setThreeDSChallenge(null);
      setMessage("Verificamos el pago con Mercado Pago.");
      await Promise.all([load(), loadProjects()]);
    } catch (err) {
      const nextMessage = err?.message || "No se pudo verificar el pago";
      setCardError(nextMessage);
      setError(nextMessage);
    } finally {
      setWorking(false);
    }
  };

  const updatePaymentMethod = async () => {
    const response = await run(
      () =>
        planBillingApi.updatePaymentMethod(projectId, {
          payerEmail: normalizeEmail(payerEmail),
        }),
      "Abrimos Mercado Pago para cambiar la tarjeta.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };

  const pauseSubscription = async () => {
    if (!window.confirm("¿Pausar el plan? La landing seguirá visible, pero las funciones operativas quedarán pausadas hasta que lo reactives.")) return;
    await run(() => planBillingApi.pause(projectId), "Tu plan quedó pausado.");
  };

  const cancelSubscription = async () => {
    if (!window.confirm("¿Cancelar el plan y detener el cobro mensual de WebProfit? La landing seguirá visible, pero las funciones operativas quedarán pausadas.")) return;
    await run(() => planBillingApi.cancel(projectId), "Tu plan quedó cancelado.");
  };

  const actionLabel = pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation
    ? "Continuar en Mercado Pago"
    : paymentMode === "one_time"
      ? "Pagar con Mercado Pago"
      : paymentNeedsAttention
        ? "Actualizar tarjeta con Mercado Pago"
        : isActive && billing.isRecurring
          ? selectionChanged || priceNeedsConfirmation
            ? "Actualizar suscripción"
            : "Suscripción activa"
          : "Suscribirme con Mercado Pago";
  const primaryAction =
    paymentNeedsAttention && paymentMode === "recurring" && canControlProvider
      ? updatePaymentMethod
      : saveSelection;
  const modeLocked =
    (paymentMode === "one_time" && oneTimeLocked) ||
    (paymentMode === "recurring" && recurringLocked);
  const primaryDisabled =
    working ||
    !canSubmit ||
    !payerEmailReady ||
    modeLocked ||
    (!paymentNeedsAttention && !hasCheckoutAction);
  const embeddedCardConfigured = Boolean(
    data?.embeddedCardConfigured && data?.mercadoPagoPublicKey,
  );
  const cardDisabled =
    working ||
    !canSubmit ||
    modeLocked ||
    checkoutAmount <= 0 ||
    !embeddedCardConfigured;


  if (loading && !data) {
    return <DashboardLoadingState label="Preparando tu plan…" minHeight={420} />;
  }

  return (
    <Stack spacing={2.5} sx={{ pb: 4, minWidth: 0 }}>
      <Paper
        className={embedded ? "wp-settings-plan-hero" : undefined}
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.25, sm: 3, md: 3.5 },
          borderRadius: 4,
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#2A4052 72%,#35566A 100%)",
          boxShadow: "0 18px 46px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.16)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 250,
            height: 250,
            borderRadius: "50%",
            right: -90,
            top: -155,
            bgcolor: "rgba(243,90,58,.26)",
          }}
        />
        <Stack position="relative" spacing={2.5} sx={{ minWidth: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "flex-start" }}
            spacing={2}
            sx={{ minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: ".13em", color: "rgba(255,255,255,.62)" }}>
                TU PLAN WEBPROFIT
              </Typography>
              <Typography
                variant={embedded ? "h4" : "h3"}
                sx={{
                  mt: 0.2,
                  fontWeight: 950,
                  letterSpacing: "-.04em",
                  lineHeight: 1.08,
                  overflowWrap: "anywhere",
                }}
              >
                Elegí lo que necesita tu negocio
              </Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mt: 1, maxWidth: 720, color: "rgba(255,255,255,.75)", lineHeight: 1.55 }}>
                Armá tu plan, elegí débito automático o un pago por período y completá todo dentro de Mercado Pago.
              </Typography>
              <ContextualHelp
                compact
                dark
                label="Cómo funciona"
                title="Cómo se arma y cobra el plan"
                text="Elegí capacidades, revisá el total y después elegí débito automático o pago por período. Todo ahorro se muestra antes de pagar y Mercado Pago confirma el cobro antes de cambiar el acceso."
                buttonSx={{ display: { xs: "inline-flex", sm: "none" }, mt: .7 }}
              />
            </Box>
            <Chip
              icon={<WorkspacePremiumRoundedIcon />}
              label={billingAccess.isGrace
                ? "Pago pendiente · período de gracia"
                : paymentSuspended
                  ? "Suspendido por pago"
                  : billingAccess.isLandingOnly
                    ? "Landing visible · funciones pausadas"
                  : trialActive
                    ? `Prueba gratis · ${billingAccess.trialDaysRemaining ?? 30} días`
                    : statusLabel(billing.status)}
              color={billingAccess.isLandingOnly || paymentSuspended ? "error" : billingAccess.isGrace ? "warning" : statusColor(billing.status)}
              sx={{
                maxWidth: "100%",
                fontWeight: 950,
                alignSelf: { xs: "flex-start", md: "center" },
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          </Stack>

          <Box
            className={embedded ? "wp-settings-plan-steps" : undefined}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3,minmax(0,1fr))",
              gap: { xs: .7, sm: 2 },
            }}
          >
            <Step number="1" mobileLabel="Capacidades">Elegí capacidades</Step>
            <Step number="2" mobileLabel="Total">Revisá el total</Step>
            <Step number="3" mobileLabel="Pago">Elegí cómo pagar</Step>
          </Box>
        </Stack>
      </Paper>

      <Alert severity={isActive || trialActive ? "success" : "info"} sx={{ borderRadius: 3, display: trialActive ? "flex" : { xs: "none", sm: "flex" } }}>
        {isActive
          ? "Tu suscripción activa también habilita un segundo proyecto para esta cuenta."
          : trialActive
            ? `Estás en la prueba gratis completa de 30 días. Durante este período también podés crear un segundo proyecto para la misma cuenta. Todas las capacidades funcionan hasta el ${dateLabel(billingAccess.trialEndsAt)}; después podés conservar sólo las que elijas.`
            : "Al activar una suscripción, además de las capacidades elegidas, habilitás un segundo proyecto para tu cuenta."}
      </Alert>

      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")} sx={{ borderRadius: 3 }}>{message}</Alert> : null}
      {featureRequested && !paymentNeedsAttention ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Llegaste para activar <strong>{featureRequested}</strong>. Buscalo abajo y tocá <strong>Agregar al plan</strong>.
        </Alert>
      ) : null}
      {!data?.mercadoPagoConfigured ? (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          El cobro todavía no está disponible porque falta configurar el Mercado Pago de WebProfit. No se modificará tu plan hasta que esté listo.
        </Alert>
      ) : null}
      {billing.pricingOutdated ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Hay un nuevo precio para esta combinación. Revisá el total y confirmalo antes de cambiar el próximo cobro.
        </Alert>
      ) : null}
      {pendingPriceChanged ? (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          El pago pendiente tiene otro importe. Al confirmar se generará una autorización con el total que ves ahora.
        </Alert>
      ) : null}
      {billingAccess.isGrace ? (
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(237,108,2,.24)" }}>
          <Typography sx={{ fontWeight: 950 }}>Tu proyecto sigue funcionando, pero hay un pago pendiente.</Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            {billing.paymentMode === "one_time"
              ? `Pagá otro período antes del ${dateLabel(billingAccess.graceUntil)}. Quedan ${billingAccess.daysRemaining ?? "pocos"} días para evitar que se pausen las funciones operativas.`
              : `Actualizá la tarjeta antes del ${dateLabel(billingAccess.graceUntil)}. Quedan ${billingAccess.daysRemaining ?? "pocos"} días para evitar que se pausen las funciones operativas.`}
          </Typography>
        </Alert>
      ) : null}
      {paymentSuspended ? (
        <Alert severity="error" icon={<LockClockRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(211,47,47,.22)" }}>
          <Typography sx={{ fontWeight: 950 }}>El proyecto está suspendido por pago.</Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            Tus datos siguen seguros. Regularizá la suscripción desde esta pantalla para recuperar el acceso al proyecto y sus herramientas.
          </Typography>
        </Alert>
      ) : null}
      {billingAccess.isLandingOnly ? (
        <Alert severity="error" icon={<LockClockRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(211,47,47,.22)" }}>
          <Typography sx={{ fontWeight: 950 }}>
            {billingAccess.reason === "trial_expired"
              ? "La prueba gratis de 30 días terminó; la landing sigue visible."
              : "La landing sigue visible; el resto está pausado."}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            {billingAccess.reason === "trial_expired"
              ? "Tus datos no se borraron. Elegí las capacidades que querés conservar y WebProfit reactivará automáticamente las funciones al aprobarse el pago."
              : billing.paymentMode === "one_time"
                ? "Tus datos no se borraron. Pagá un nuevo período y WebProfit reactivará automáticamente las funciones."
                : "Tus datos no se borraron. Cambiá la tarjeta o verificá el pago y WebProfit reactivará automáticamente las funciones."}
          </Typography>
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" },
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <SummaryMetric
          icon={<CreditCardRoundedIcon />}
          title={paymentNeedsAttention ? "IMPORTE A REGULARIZAR" : "IMPORTE DEL PLAN"}
          value={money(isActive || paymentNeedsAttention ? billing.monthlyAmount : selectedAmount, isActive || paymentNeedsAttention ? billing.currency : currency)}
          helper={effectivePaymentMode === "one_time" ? "Valor de un período mensual" : "Mensualidad del débito automático"}
        />
        <SummaryMetric
          icon={billingAccess.isLandingOnly || paymentSuspended ? <LockClockRoundedIcon /> : <EventAvailableRoundedIcon />}
          title={trialActive ? "PRUEBA GRATIS HASTA" : billingAccess.isGrace ? "TENÉS HASTA" : billingAccess.isLandingOnly || paymentSuspended ? "ACCESO ACTUAL" : effectivePaymentMode === "one_time" ? "SERVICIO ACTIVO HASTA" : "PRÓXIMO COBRO"}
          value={trialActive ? dateLabel(billingAccess.trialEndsAt) : billingAccess.isGrace ? dateLabel(billingAccess.graceUntil) : paymentSuspended ? "Suspendido" : billingAccess.isLandingOnly ? "Solo landing" : dateLabel(billing.nextPaymentDate || billing.currentPeriodEnd)}
          helper={trialActive ? "Acceso completo sin tarjeta" : billingAccess.isGrace ? "Después se pausarán funciones" : billingAccess.isLandingOnly || paymentSuspended ? "Se reactiva al aprobarse el pago" : effectivePaymentMode === "one_time" ? "No se renueva automáticamente" : "Renovación mensual automática"}
        />
        <SummaryMetric
          icon={<AutorenewRoundedIcon />}
          sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
          title="FORMA DE PAGO"
          value={effectivePaymentMode === "one_time" ? "Pago por período" : billing.isRecurring ? "Débito automático" : "A elegir"}
          helper={effectivePaymentMode === "one_time" ? "Un solo cobro con tarjeta o saldo disponible" : paymentNeedsAttention ? "Actualizá la tarjeta para continuar" : billing.isRecurring ? "Cobro mensual automático" : "Se configura al activar el plan"}
        />
      </Box>

      <Box>
        {ownerFullSystem ? <Alert severity="success" sx={{ mb: 1.5 }}>
          <b>Precio promocional activado.</b> <b>Módulos activados por el propietario.</b> Pagás un único total de {money(selectedAmount, billing.currency || "ARS")}/mes; el importe que ves en cada módulo es solamente el reparto interno de ese total y no se suma nuevamente.
        </Alert> : null}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-end" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.03em", lineHeight: 1.15 }}>
                {ownerFullSystem ? "Módulos activados por el propietario" : paymentNeedsAttention ? "Tu plan actual" : "Armá tu plan"}
              </Typography>
              {!ownerFullSystem ? <ContextualHelp
                compact
                title="Qué cambia al activar una capacidad"
                text="Cada capacidad habilita una parte concreta de WebProfit. Si una depende de otra, WebProfit agrega esa dependencia antes de calcular el total. Combinar capacidades elegibles puede bajar automáticamente el precio."
                bullets={[
                  "El precio mostrado se recalcula con la combinación seleccionada.",
                  "Desactivar una capacidad puede requerir quitar primero las que dependen de ella.",
                  "El tipo de negocio orienta la experiencia, pero no te encierra en una sola combinación de capacidades.",
                ]}
              /> : null}
            </Stack>
            <Typography sx={{ mt: 0.55, color: BRAND.muted, maxWidth: 760, lineHeight: 1.5 }}>
              {ownerFullSystem
                ? "Este proyecto tiene un acuerdo comercial administrado por el propietario. Los módulos están bloqueados y no podés activarlos ni desactivarlos desde acá. El precio de cada fila es un reparto informativo del precio promocional total."
                : paymentNeedsAttention
                  ? "Conservamos tus capacidades y tus datos mientras regularizás el pago."
                  : "Elegí las capacidades que necesitás. Si una depende de otra, WebProfit suma esa dependencia y recalcula el total. Los descuentos por combinación se aplican solos y se muestran antes de pagar."}
            </Typography>
          </Box>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={ownerFullSystem ? `${selectedCodes.length} módulos incluidos` : `${selectedCodes.length} elegidos`}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              fontWeight: 900,
              alignSelf: { md: "center" },
            }}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0,1fr)", lg: "minmax(0,1fr) 330px" },
            gap: { xs: 2.25, lg: 2.75 },
            alignItems: "start",
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {services.length ? (
              <>
                <Box sx={{ display: { xs: "block", md: "none" }, minWidth: 0 }}>
                  <Box
                    sx={{
                      mb: 1,
                      px: 0.2,
                      minHeight: 34,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".94rem", lineHeight: 1.2 }}>
                      {selectedCodes.length} {selectedCodes.length === 1 ? "módulo" : "módulos"} · {money(selectedAmount, currency)}
                      <Box component="span" sx={{ ml: 0.35, color: BRAND.muted, fontWeight: 800, fontSize: ".78rem" }}>
                        /mes
                      </Box>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" },
                      gap: { xs: 1, sm: 1.15 },
                      minWidth: 0,
                      "@media (max-width:350px)": { gridTemplateColumns: "minmax(0,1fr)" },
                    }}
                  >
                    {serviceOptions.map((option) => (
                      <MobileServiceTile
                        key={option.service.code}
                        {...option}
                        disabled={working || paymentNeedsAttention || !data?.canManage || ownerFullSystem}
                        paymentNeedsAttention={paymentNeedsAttention}
                        onToggle={(checked) => toggleService(option.service, checked)}
                      />
                    ))}
                  </Box>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    display: { xs: "none", md: "block" },
                    minWidth: 0,
                    overflow: "hidden",
                    borderRadius: 3.5,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: "0 10px 30px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.055)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "44px minmax(220px,1fr) minmax(120px,.55fr) 132px 92px",
                      gap: 1.75,
                      alignItems: "center",
                      px: 2,
                      py: 1.1,
                      bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
                      borderBottom: `1px solid ${BRAND.border}`,
                    }}
                  >
                    <Box />
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
                      CAPACIDAD
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
                      INFORMACIÓN
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, textAlign: "right" }}>
                      {ownerFullSystem ? "PARTE DEL TOTAL" : "PRECIO"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, textAlign: "center" }}>
                      ESTADO
                    </Typography>
                  </Box>

                  {serviceOptions.map((option) => (
                    <ServiceRow
                      key={option.service.code}
                      {...option}
                      disabled={working || paymentNeedsAttention || !data?.canManage || ownerFullSystem}
                      paymentNeedsAttention={paymentNeedsAttention}
                      onToggle={(checked) => toggleService(option.service, checked)}
                    />
                  ))}
                </Paper>
              </>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)",
                }}
              >
                <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                  No hay capacidades comerciales disponibles para configurar.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: BRAND.muted }}>
                  Cuando haya capacidades habilitadas para este proyecto, vas a poder elegirlas desde esta sección.
                </Typography>
              </Paper>
            )}
          </Box>

          <CheckoutSummary
            selectedServices={displayedSelectedServices}
            selectedAmount={selectedAmount}
            monthlyPricing={monthlyPricing}
            periodPricing={periodPricing}
            periodMonths={periodMonths}
            periodOptions={periodOptions}
            onPeriodMonthsChange={setPeriodMonths}
            currency={currency}
            billing={billing}
            billingAccess={billingAccess}
            paymentNeedsAttention={paymentNeedsAttention}
            paymentMode={paymentMode}
            onPaymentModeChange={changePaymentMode}
            payerEmail={payerEmail}
            onPayerEmailChange={setPayerEmail}
            payerEmailRequired={payerEmailRequired}
            recurringLocked={recurringLocked}
            oneTimeLocked={oneTimeLocked}
            selectionChanged={selectionChanged}
            actionLabel={actionLabel}
            primaryDisabled={primaryDisabled}
            primaryAction={primaryAction}
            working={working}
            mercadoPagoPublicKey={data?.mercadoPagoPublicKey || ""}
            embeddedCardConfigured={embeddedCardConfigured}
            cardDisabled={cardDisabled}
            onCardSubmit={payWithEmbeddedCard}
            cardError={cardError}
            threeDSChallenge={threeDSChallenge}
            onThreeDSComplete={completeThreeDSChallenge}
            recurringActive={Boolean(isActive && billing.isRecurring)}
          />
        </Box>
      </Box>

      {(canControlProvider || paymentNeedsAttention) ? (
        <Paper elevation={0} sx={{ p: { xs: 2.25, md: 2.75 }, borderRadius: 3.5, border: `1px solid ${BRAND.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                {paymentNeedsAttention
                  ? paymentMode === "one_time"
                    ? "Renovar el período"
                    : "Regularizar el débito automático"
                  : "Administrar el débito automático"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: BRAND.muted }}>
                {paymentNeedsAttention
                  ? paymentMode === "one_time"
                    ? "Pagá un período sin guardar una renovación automática. Al aprobarse, las funciones se reactivan."
                    : "Actualizá la tarjeta o verificá el cobro pendiente para mantener el servicio activo."
                  : "Verificá el estado, cambiá la tarjeta o administrá la renovación mensual."}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              flexWrap={{ xs: "nowrap", sm: "wrap" }}
              useFlexGap
              sx={{ width: { xs: "100%", md: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}
            >
              {paymentNeedsAttention ? (
                <>
                  <Button
                    disabled={working || !data?.canManage || !data?.mercadoPagoConfigured || selectedCodes.length === 0 || modeLocked || (paymentMode === "recurring" && !isValidEmail(payerEmail))}
                    startIcon={<CreditCardRoundedIcon />}
                    onClick={primaryAction}
                    variant="contained"
                    sx={{ bgcolor: BRAND.orange, borderRadius: 2.5, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: BRAND.orangeDark } }}
                  >
                    {paymentMode === "one_time" ? "Pagar un período" : "Cambiar tarjeta"}
                  </Button>
                  <Button
                    disabled={working}
                    startIcon={<SyncRoundedIcon />}
                    onClick={() => run(() => planBillingApi.refresh(projectId), "Estado actualizado.")}
                    sx={{ textTransform: "none", fontWeight: 900 }}
                  >
                    Ya pagué, verificar
                  </Button>
                </>
              ) : canControlProvider ? (
                billing.status === "paused" ? (
                  <Button
                    disabled={working || !data?.canManage}
                    startIcon={<PlayCircleRoundedIcon />}
                    onClick={() => run(() => planBillingApi.resume(projectId), "Tu plan quedó activo nuevamente.")}
                    variant="contained"
                    sx={{ bgcolor: BRAND.orange, borderRadius: 2.5, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}
                  >
                    Reactivar plan
                  </Button>
                ) : (
                  <Accordion
                    elevation={0}
                    disableGutters
                    sx={{ width: { xs: "100%", sm: 320 }, border: `1px solid ${BRAND.border}`, borderRadius: "12px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: .8 } }}>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Opciones de suscripción</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={.5} sx={{ "& .MuiButton-root": { justifyContent: "flex-start", width: "100%", textTransform: "none", fontWeight: 850 } }}>
                        <Button disabled={working} startIcon={<SyncRoundedIcon />} onClick={() => run(() => planBillingApi.refresh(projectId), "Estado actualizado.")}>
                          Verificar estado
                        </Button>
                        <Button disabled={working || !data?.canManage || !isValidEmail(payerEmail)} startIcon={<CreditCardRoundedIcon />} onClick={updatePaymentMethod}>
                          Cambiar tarjeta
                        </Button>
                        <Button disabled={working || !data?.canManage || billing.status === "cancelled"} startIcon={<PauseCircleRoundedIcon />} onClick={pauseSubscription}>
                          Pausar renovación
                        </Button>
                        <Button disabled={working || !data?.canManage || billing.status === "cancelled"} color="error" startIcon={<CancelRoundedIcon />} onClick={cancelSubscription}>
                          Cancelar plan
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Accordion elevation={0} disableGutters sx={{ borderRadius: "14px !important", border: `1px solid ${BRAND.border}`, overflow: "hidden", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: { xs: 2.25, md: 3 }, py: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              Historial de pagos y cambios
            </Typography>
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, color: BRAND.muted }}>
              Revisá autorizaciones, importes y estados anteriores.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.25, md: 3 }, pb: 3 }}>
          <TableContainer sx={{ maxWidth: "100%" }}>
            <ResponsiveOperationalTable size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Plan elegido</TableCell>
                  <TableCell>Modalidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((entry) => (
                  <TableRow key={`${entry.historyType}-${entry.id || entry._id}`} hover>
                    <TableCell>{dateLabel(entry.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{subscriptionServicesLabel(entry)}</TableCell>
                    <TableCell>
                      {entry.historyType === "one_time"
                        ? "Pago por período"
                        : entry.provider === "mercadopago"
                          ? "Débito automático"
                          : "Manual"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(entry.status)} color={statusColor(entry.status)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {money(entry.amount, entry.currency)}
                    </TableCell>
                  </TableRow>
                ))}
                {!billingHistory.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>Todavía no hay pagos o cambios registrados.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
